import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { assign as radashAssign } from 'radash';

import { BaseChartComponent } from '@connect-services/client/components/statistics/base-chart/base-chart.component';
import { ChartLayoutComponent } from '@connect-services/client/components/statistics/chart-layout/chart-layout.component';
import {
    BaseChartOptions,
    ChartComponentInterface,
    collapsedHeightChart,
    commonOptions,
    expandedPieGaugeHeightChart,
} from '@connect-services/client/services/chart.service';
import { ComputedStatistic, LabelOptions, SimpleValueData } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [BaseChartComponent, ChartLayoutComponent, MatTooltip, RessourcesModule],
    selector: 'app-gauge-chart',
    styleUrl: './gauge-chart.component.scss',
    templateUrl: './gauge-chart.component.html',
})
export class GaugeChartComponent implements ChartComponentInterface {
    public readonly computedStatistic = input.required<ComputedStatistic>();
    public readonly half = input(false);
    public readonly isExpanded = input.required<boolean>();
    public readonly isPrintScreen = input<boolean>(false);
    public readonly isStatisticScreen = input<boolean>(true);
    public readonly isTitleCentred = input<boolean>(false);

    public readonly options = computed<BaseChartOptions>(() => {
        const computedStatistic = this.computedStatistic();
        const value = (computedStatistic?.result?.data as SimpleValueData) || { x: 0 };

        return radashAssign(commonOptions, {
            chart: {
                animations: {
                    enabled: !this.isPrintScreen(),
                },
                events: {
                    mounted: () => {
                        this.ready.emit();
                    },
                },
                height: this.isExpanded() ? expandedPieGaugeHeightChart : collapsedHeightChart,
                sparkline: {
                    enabled: true,
                },
                type: 'radialBar',
            },
            dataLabels: {
                enabled: this.isPrintScreen(),
            },
            fill: {
                colors: computedStatistic.renderOptions.colors ?? [],
            },
            labels: computedStatistic.renderOptions.title ? [computedStatistic.renderOptions.title] : [],
            plotOptions: {
                radialBar: {
                    dataLabels: {
                        name: {
                            fontSize: '16px',
                            show: false,
                        },
                        value: {
                            color: computedStatistic?.renderOptions?.colors?.[0] ?? '#000',
                            fontSize: this.half() ? '22px' : '32px',
                            formatter(val: number): string {
                                if (computedStatistic.renderOptions.labels?.type === LabelOptions.TypeEnum.PERCENTAGE) {
                                    return `${val}%`;
                                }

                                return `${val}`;
                            },
                            offsetY: this.isStatisticScreen() ? 10 : 0,
                            show: true,
                        },
                    },
                    endAngle: this.half() ? 90 : 360,
                    hollow: {
                        size: this.isStatisticScreen() ? '65%' : '45%',
                    },
                    startAngle: this.half() ? -90 : 0,
                    track: {
                        background: '#e7e7e7',
                    },
                },
            },
            series: [value.x],
            stroke: {
                lineCap: this.isStatisticScreen() ? 'round' : 'butt',
            },
        });
    });
    public ready = output<void>();
    public readonly tooltipMessage = input('');
    public readonly translateName = input<boolean>(false);
}
